import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from './apiClient';

const MAX_ITEMS = 80;

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await apiJson('/notifications');
      setItems(Array.isArray(data.notifications) ? data.notifications.slice(0, MAX_ITEMS) : []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setItems([]);
      return;
    }
    void load();
  }, [user, load]);

  const push = useCallback(async (payload) => {
    try {
      const data = await apiJson('/notifications', {
        method: 'POST',
        body: JSON.stringify({
          title: payload.title || 'cbonline app',
          body: payload.body || '',
          navigateTo: payload.navigateTo || null,
        }),
      });
      const n = data.notification;
      if (n) {
        setItems((prev) => [n, ...prev].slice(0, MAX_ITEMS));
      }
    } catch {
      /* ignore */
    }
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await apiJson(`/notifications/${id}/read`, { method: 'POST', body: '{}' });
      setItems((prev) => prev.map((n) => (String(n.id) === String(id) ? { ...n, read: true } : n)));
    } catch {
      /* ignore */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await apiJson('/notifications/read-all', { method: 'POST', body: '{}' });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  }, []);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const value = useMemo(
    () => ({ items, push, markRead, markAllRead, unreadCount, refreshNotifications: load }),
    [items, push, markRead, markAllRead, unreadCount, load]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    return {
      items: [],
      push: async () => {},
      markRead: async () => {},
      markAllRead: async () => {},
      unreadCount: 0,
      refreshNotifications: async () => {},
    };
  }
  return ctx;
}
